import { useState, useMemo } from 'react';

interface ContractorFinderProps {
  darkMode: boolean;
  locationLabel: string;
  locationState: string;
  estimatedTotal: number | null;
}

interface Contractor {
  id: string;
  name: string;
  specialty: string[];
  rating: number;
  reviews: number;
  yearsExp: number;
  priceRange: 'Budget' | 'Mid-Range' | 'Premium' | 'Luxury';
  phone: string;
  badge?: string;
  badgeColor?: string;
  avatar: string;
}

// Contractor data keyed by state
const CONTRACTORS_BY_STATE: Record<string, Contractor[]> = {
  NY: [
    { id: 'ny1', name: 'Skyline Build Group', specialty: ['Luxury Residential', 'High-Rise'], rating: 4.9, reviews: 312, yearsExp: 22, priceRange: 'Luxury', phone: '(212) 555-0182', badge: 'Top Rated', badgeColor: 'amber', avatar: 'SB' },
    { id: 'ny2', name: 'Manhattan Craft Builders', specialty: ['Custom Homes', 'Renovations'], rating: 4.7, reviews: 198, yearsExp: 15, priceRange: 'Premium', phone: '(212) 555-0247', badge: 'Verified', badgeColor: 'teal', avatar: 'MC' },
    { id: 'ny3', name: 'Brooklyn Home Co.', specialty: ['Brownstones', 'Modern Lofts'], rating: 4.6, reviews: 145, yearsExp: 11, priceRange: 'Mid-Range', phone: '(718) 555-0391', avatar: 'BH' },
  ],
  CA: [
    { id: 'ca1', name: 'Pacific Coast Builders', specialty: ['Eco Homes', 'Modern Design'], rating: 4.9, reviews: 428, yearsExp: 18, priceRange: 'Luxury', phone: '(415) 555-0163', badge: 'Top Rated', badgeColor: 'amber', avatar: 'PC' },
    { id: 'ca2', name: 'SoCal Premier Homes', specialty: ['Mediterranean', 'Custom Pools'], rating: 4.8, reviews: 267, yearsExp: 20, priceRange: 'Premium', phone: '(310) 555-0284', badge: 'Verified', badgeColor: 'teal', avatar: 'SP' },
    { id: 'ca3', name: 'Bay Area Build Co.', specialty: ['Smart Homes', 'Hillside'], rating: 4.7, reviews: 189, yearsExp: 14, priceRange: 'Premium', phone: '(650) 555-0317', avatar: 'BA' },
  ],
  TX: [
    { id: 'tx1', name: 'Lone Star Construction', specialty: ['Ranch Homes', 'Large Estates'], rating: 4.8, reviews: 356, yearsExp: 25, priceRange: 'Mid-Range', phone: '(512) 555-0142', badge: 'Top Rated', badgeColor: 'amber', avatar: 'LS' },
    { id: 'tx2', name: 'Texas Custom Homes', specialty: ['Modern Farmhouse', 'Custom'], rating: 4.7, reviews: 221, yearsExp: 17, priceRange: 'Mid-Range', phone: '(214) 555-0278', badge: 'Verified', badgeColor: 'teal', avatar: 'TC' },
    { id: 'tx3', name: 'Houston Build Masters', specialty: ['Energy Efficient', 'New Build'], rating: 4.5, reviews: 167, yearsExp: 12, priceRange: 'Budget', phone: '(713) 555-0394', avatar: 'HB' },
  ],
  WA: [
    { id: 'wa1', name: 'Cascade Builders NW', specialty: ['Pacific NW Style', 'Timber Frame'], rating: 4.9, reviews: 203, yearsExp: 19, priceRange: 'Premium', phone: '(206) 555-0155', badge: 'Top Rated', badgeColor: 'amber', avatar: 'CB' },
    { id: 'wa2', name: 'Seattle Modern Homes', specialty: ['Contemporary', 'Green Build'], rating: 4.7, reviews: 178, yearsExp: 13, priceRange: 'Premium', phone: '(206) 555-0263', badge: 'Verified', badgeColor: 'teal', avatar: 'SM' },
    { id: 'wa3', name: 'Puget Sound Contractors', specialty: ['Waterfront', 'Custom'], rating: 4.6, reviews: 134, yearsExp: 10, priceRange: 'Mid-Range', phone: '(253) 555-0381', avatar: 'PS' },
  ],
  MA: [
    { id: 'ma1', name: 'New England Build Co.', specialty: ['Colonial', 'Cape Cod'], rating: 4.8, reviews: 245, yearsExp: 28, priceRange: 'Premium', phone: '(617) 555-0171', badge: 'Top Rated', badgeColor: 'amber', avatar: 'NE' },
    { id: 'ma2', name: 'Boston Premier Homes', specialty: ['Historic Renovation', 'Custom'], rating: 4.7, reviews: 189, yearsExp: 16, priceRange: 'Premium', phone: '(617) 555-0289', badge: 'Verified', badgeColor: 'teal', avatar: 'BP' },
    { id: 'ma3', name: 'Cape Cod Builders', specialty: ['Coastal', 'Shingle Style'], rating: 4.6, reviews: 122, yearsExp: 14, priceRange: 'Mid-Range', phone: '(508) 555-0347', avatar: 'CC' },
  ],
  IL: [
    { id: 'il1', name: 'Midwest Build Group', specialty: ['Modern', 'Custom Homes'], rating: 4.7, reviews: 287, yearsExp: 20, priceRange: 'Mid-Range', phone: '(312) 555-0164', badge: 'Top Rated', badgeColor: 'amber', avatar: 'MB' },
    { id: 'il2', name: 'Chicago Custom Homes', specialty: ['Urban Infill', 'Luxury'], rating: 4.6, reviews: 198, yearsExp: 15, priceRange: 'Premium', phone: '(312) 555-0273', badge: 'Verified', badgeColor: 'teal', avatar: 'CH' },
    { id: 'il3', name: 'Prairie State Builders', specialty: ['Craftsman', 'Ranch'], rating: 4.5, reviews: 143, yearsExp: 11, priceRange: 'Mid-Range', phone: '(847) 555-0392', avatar: 'PS' },
  ],
  FL: [
    { id: 'fl1', name: 'Sunshine State Builders', specialty: ['Mediterranean', 'Coastal'], rating: 4.8, reviews: 334, yearsExp: 22, priceRange: 'Premium', phone: '(305) 555-0148', badge: 'Top Rated', badgeColor: 'amber', avatar: 'SS' },
    { id: 'fl2', name: 'Miami Luxury Homes', specialty: ['Modern', 'Waterfront'], rating: 4.7, reviews: 256, yearsExp: 18, priceRange: 'Luxury', phone: '(305) 555-0267', badge: 'Verified', badgeColor: 'teal', avatar: 'ML' },
    { id: 'fl3', name: 'Florida Custom Build', specialty: ['Hurricane Resistant', 'Custom'], rating: 4.5, reviews: 178, yearsExp: 14, priceRange: 'Mid-Range', phone: '(407) 555-0385', avatar: 'FC' },
  ],
  CO: [
    { id: 'co1', name: 'Rocky Mountain Builders', specialty: ['Mountain Homes', 'A-Frame'], rating: 4.8, reviews: 198, yearsExp: 17, priceRange: 'Mid-Range', phone: '(303) 555-0152', badge: 'Top Rated', badgeColor: 'amber', avatar: 'RM' },
    { id: 'co2', name: 'Denver Custom Homes', specialty: ['Modern', 'Sustainable'], rating: 4.7, reviews: 167, yearsExp: 13, priceRange: 'Mid-Range', phone: '(720) 555-0261', badge: 'Verified', badgeColor: 'teal', avatar: 'DC' },
    { id: 'co3', name: 'Colorado Build Co.', specialty: ['Ranch', 'Farmhouse'], rating: 4.5, reviews: 134, yearsExp: 10, priceRange: 'Budget', phone: '(970) 555-0378', avatar: 'CB' },
  ],
  US: [
    { id: 'us1', name: 'National Build Partners', specialty: ['All Styles', 'Custom Homes'], rating: 4.7, reviews: 512, yearsExp: 30, priceRange: 'Mid-Range', phone: '(800) 555-0100', badge: 'Top Rated', badgeColor: 'amber', avatar: 'NB' },
    { id: 'us2', name: 'American Home Builders', specialty: ['New Construction', 'Renovation'], rating: 4.6, reviews: 389, yearsExp: 25, priceRange: 'Mid-Range', phone: '(800) 555-0200', badge: 'Verified', badgeColor: 'teal', avatar: 'AH' },
    { id: 'us3', name: 'Premier Home Group', specialty: ['Luxury', 'Custom'], rating: 4.5, reviews: 267, yearsExp: 20, priceRange: 'Premium', phone: '(800) 555-0300', avatar: 'PH' },
  ],
};

// Fallback for states not in the map
const getFallbackContractors = (state: string): Contractor[] => [
  { id: `${state}1`, name: `${state} Premier Builders`, specialty: ['Custom Homes', 'New Construction'], rating: 4.7, reviews: 189, yearsExp: 15, priceRange: 'Mid-Range', phone: '(800) 555-0101', badge: 'Top Rated', badgeColor: 'amber', avatar: 'PB' },
  { id: `${state}2`, name: `${state} Custom Homes`, specialty: ['Renovations', 'Modern Design'], rating: 4.6, reviews: 143, yearsExp: 12, priceRange: 'Mid-Range', phone: '(800) 555-0202', badge: 'Verified', badgeColor: 'teal', avatar: 'CH' },
  { id: `${state}3`, name: `${state} Build Group`, specialty: ['Budget Friendly', 'Efficient'], rating: 4.4, reviews: 98, yearsExp: 8, priceRange: 'Budget', phone: '(800) 555-0303', avatar: 'BG' },
];

const PRICE_TIER_COLORS: Record<string, string> = {
  Budget: 'text-emerald-500',
  'Mid-Range': 'text-teal-500',
  Premium: 'text-amber-500',
  Luxury: 'text-rose-500',
};

const PRICE_TIER_BG: Record<string, { light: string; dark: string }> = {
  Budget: { light: 'bg-emerald-50 text-emerald-700 border-emerald-200', dark: 'bg-emerald-900/20 text-emerald-400 border-emerald-700/40' },
  'Mid-Range': { light: 'bg-teal-50 text-teal-700 border-teal-200', dark: 'bg-teal-900/20 text-teal-400 border-teal-700/40' },
  Premium: { light: 'bg-amber-50 text-amber-700 border-amber-200', dark: 'bg-amber-900/20 text-amber-400 border-amber-700/40' },
  Luxury: { light: 'bg-rose-50 text-rose-700 border-rose-200', dark: 'bg-rose-900/20 text-rose-400 border-rose-700/40' },
};

function StarRating({ rating, darkMode }: { rating: number; darkMode: boolean }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map(s => (
        <i
          key={s}
          className={`text-xs ${s <= Math.floor(rating) ? 'ri-star-fill text-amber-400' : s - 0.5 <= rating ? 'ri-star-half-fill text-amber-400' : darkMode ? 'ri-star-line text-gray-600' : 'ri-star-line text-gray-300'}`}
        ></i>
      ))}
    </div>
  );
}

export default function ContractorFinder({ darkMode, locationLabel, locationState, estimatedTotal }: ContractorFinderProps) {
  const [expanded, setExpanded] = useState(false);
  const [contactedId, setContactedId] = useState<string | null>(null);
  const [toast, setToast] = useState('');

  const contractors = useMemo(() => {
    return CONTRACTORS_BY_STATE[locationState] || getFallbackContractors(locationState);
  }, [locationState]);

  const text = darkMode ? 'text-white' : 'text-gray-900';
  const subtext = darkMode ? 'text-gray-400' : 'text-gray-500';

  const handleContact = (contractor: Contractor) => {
    setContactedId(contractor.id);
    setToast(`Request sent to ${contractor.name}!`);
    setTimeout(() => setToast(''), 2500);
  };

  const fmt = (n: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(n);

  return (
    <div className={`rounded-xl border ${darkMode ? 'bg-gray-800/60 border-gray-700' : 'bg-white border-gray-200'}`}>
      {/* Header — always visible */}
      <button
        onClick={() => setExpanded(v => !v)}
        className="w-full flex items-center justify-between p-4 cursor-pointer"
      >
        <div className="flex items-center gap-3">
          <div className={`w-9 h-9 flex items-center justify-center rounded-xl ${darkMode ? 'bg-teal-900/40' : 'bg-teal-100'}`}>
            <i className={`ri-user-star-line text-lg ${darkMode ? 'text-teal-400' : 'text-teal-600'}`}></i>
          </div>
          <div className="text-left">
            <h4 className={`text-sm font-bold ${text}`}>Contractor Finder</h4>
            <p className={`text-xs ${subtext}`}>
              {contractors.length} contractors near {locationLabel.split(',')[0]}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${darkMode ? 'bg-teal-900/30 text-teal-400' : 'bg-teal-100 text-teal-700'}`}>
            {contractors.length} found
          </span>
          <i className={`${expanded ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-lg ${subtext}`}></i>
        </div>
      </button>

      {expanded && (
        <div className="px-4 pb-4 space-y-3">
          {/* Budget match hint */}
          {estimatedTotal && (
            <div className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs ${darkMode ? 'bg-teal-900/20 border border-teal-700/40 text-teal-300' : 'bg-teal-50 border border-teal-200 text-teal-700'}`}>
              <i className="ri-lightbulb-line text-teal-500"></i>
              Showing contractors suited for a <strong>{fmt(estimatedTotal)}</strong> project
            </div>
          )}

          {/* Contractor cards */}
          {contractors.map((c, idx) => {
            const tierStyle = PRICE_TIER_BG[c.priceRange];
            const isContacted = contactedId === c.id;
            return (
              <div
                key={c.id}
                className={`rounded-xl border p-4 transition-all ${
                  idx === 0
                    ? darkMode ? 'border-amber-600/50 bg-amber-900/10' : 'border-amber-300 bg-amber-50/40'
                    : darkMode ? 'border-gray-700 bg-gray-700/30' : 'border-gray-200 bg-gray-50/50'
                }`}
              >
                <div className="flex items-start gap-3">
                  {/* Avatar */}
                  <div className={`w-10 h-10 flex items-center justify-center rounded-xl flex-shrink-0 font-bold text-sm ${
                    idx === 0
                      ? 'bg-gradient-to-br from-amber-400 to-orange-500 text-white'
                      : darkMode ? 'bg-gray-600 text-gray-300' : 'bg-gray-200 text-gray-600'
                  }`}>
                    {c.avatar}
                  </div>

                  <div className="flex-1 min-w-0">
                    {/* Name + badge */}
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className={`text-sm font-bold ${text}`}>{c.name}</span>
                      {c.badge && (
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${
                          c.badgeColor === 'amber'
                            ? darkMode ? 'bg-amber-900/30 text-amber-400 border-amber-700/40' : 'bg-amber-100 text-amber-700 border-amber-200'
                            : darkMode ? 'bg-teal-900/30 text-teal-400 border-teal-700/40' : 'bg-teal-100 text-teal-700 border-teal-200'
                        }`}>
                          <i className={`${c.badgeColor === 'amber' ? 'ri-award-line' : 'ri-shield-check-line'} mr-1`}></i>
                          {c.badge}
                        </span>
                      )}
                    </div>

                    {/* Rating */}
                    <div className="flex items-center gap-2 mb-2">
                      <StarRating rating={c.rating} darkMode={darkMode} />
                      <span className={`text-xs font-semibold ${darkMode ? 'text-amber-400' : 'text-amber-600'}`}>{c.rating}</span>
                      <span className={`text-xs ${subtext}`}>({c.reviews} reviews)</span>
                      <span className={`text-xs ${subtext}`}>· {c.yearsExp}yr exp</span>
                    </div>

                    {/* Specialties */}
                    <div className="flex flex-wrap gap-1 mb-3">
                      {c.specialty.map(s => (
                        <span key={s} className={`text-xs px-2 py-0.5 rounded-full ${darkMode ? 'bg-gray-600 text-gray-300' : 'bg-white text-gray-600 border border-gray-200'}`}>
                          {s}
                        </span>
                      ))}
                      <span className={`text-xs px-2 py-0.5 rounded-full border font-semibold ${darkMode ? tierStyle.dark : tierStyle.light}`}>
                        {c.priceRange}
                      </span>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleContact(c)}
                        disabled={isContacted}
                        className={`flex-1 py-2 rounded-lg text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${
                          isContacted
                            ? darkMode ? 'bg-teal-900/30 text-teal-400 border border-teal-700/40' : 'bg-teal-100 text-teal-700 border border-teal-200'
                            : 'bg-gradient-to-r from-teal-500 to-cyan-600 hover:from-teal-600 hover:to-cyan-700 text-white'
                        }`}
                      >
                        {isContacted ? <><i className="ri-check-line mr-1"></i>Request Sent</> : <><i className="ri-send-plane-line mr-1"></i>Get Quote</>}
                      </button>
                      <a
                        href={`tel:${c.phone}`}
                        className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold border transition-all cursor-pointer whitespace-nowrap ${darkMode ? 'border-gray-600 text-gray-300 hover:bg-gray-600' : 'border-gray-200 text-gray-600 hover:bg-gray-100'}`}
                      >
                        <i className="ri-phone-line"></i>
                        {c.phone}
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}

          {/* Disclaimer */}
          <p className={`text-xs text-center ${subtext} pt-1`}>
            <i className="ri-information-line mr-1"></i>
            Contractor listings are illustrative. Always verify licenses and get multiple quotes.
          </p>

          {/* Toast */}
          {toast && (
            <div className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-semibold ${darkMode ? 'bg-teal-900/30 text-teal-300' : 'bg-teal-50 text-teal-700'}`}>
              <i className="ri-checkbox-circle-fill text-teal-500"></i>
              {toast}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
